import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Badge } from 'components/ui/badge';
import { ChevronDownIcon, LoaderIcon, XIcon } from 'lucide-react';
import { TotemLabel } from './TotemLabel';

export type OptionValue = string | number | boolean | null;

export type Option<T extends OptionValue = string> = {
    label: string;
    value: T;
    isDisabled?: boolean;
};

export type SelectedOption<T extends OptionValue = string> = Option<T> | null | undefined;

// --- Single select ---

type TotemSelectSingleProps<T extends OptionValue> = {
    value: T | null;
    onChange: (value: T | null) => void;
    isMulti?: false;
};

// --- Multi select ---

type TotemSelectMultiProps<T extends OptionValue> = {
    value: T[];
    onChange: (value: T[]) => void;
    isMulti: true;
};

// --- Common props ---

type TotemSelectCommonProps<T extends OptionValue> = {
    options?: Option<T>[];
    label?: string;
    sublabel?: string;
    placeholder?: string;
    width?: string;
    disabled?: boolean;
    isClearable?: boolean;
    dataTest?: string;
    className?: string;
    // Async support
    fetchOptions?: (query: string) => Promise<Option<T>[]>;
    defaultOptions?: Option<T>[];
};

export type TotemSelectProps<T extends OptionValue> = TotemSelectCommonProps<T> &
    (TotemSelectSingleProps<T> | TotemSelectMultiProps<T>);

export function TotemSelect<T extends OptionValue>({
    value,
    onChange,
    options: staticOptions,
    label,
    sublabel,
    placeholder = 'Aucune valeur',
    width,
    disabled = false,
    isClearable = false,
    isMulti = false,
    dataTest,
    className,
    fetchOptions,
    defaultOptions,
}: TotemSelectProps<T>) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [asyncOptions, setAsyncOptions] = useState<Option<T>[]>(defaultOptions ?? []);
    const [loading, setLoading] = useState(false);

    const isAsync = !!fetchOptions;

    // Stabilize defaultOptions reference to avoid infinite re-renders from inline arrays
    const defaultOptionsRef = useRef(defaultOptions);
    if (
        defaultOptions?.length !== defaultOptionsRef.current?.length ||
        defaultOptions?.some((o, i) => o.value !== defaultOptionsRef.current?.[i]?.value)
    ) {
        defaultOptionsRef.current = defaultOptions;
    }
    const stableDefaultOptions = defaultOptionsRef.current;

    // Debounced async search
    useEffect(() => {
        if (!fetchOptions) return;
        if (!query) {
            setAsyncOptions(stableDefaultOptions ?? []);
            return;
        }

        setLoading(true);
        const timer = setTimeout(async () => {
            const results = await fetchOptions(query);
            setAsyncOptions(results);
            setLoading(false);
        }, 300);

        return () => {
            clearTimeout(timer);
            setLoading(false);
        };
    }, [query, fetchOptions, stableDefaultOptions]);

    const options = isAsync ? asyncOptions : (staticOptions ?? []);

    // --- Multi-select ---
    if (isMulti) {
        const onChangeMulti = onChange as (value: T[]) => void;

        const allKnownOptions = [
            ...options,
            ...(stableDefaultOptions ?? []).filter((d) => !options.some((o) => o.value === d.value)),
        ];
        // Convention: a null-valued option (e.g. "Toutes les machines") stands for an empty
        // stored value and is exclusive with specific values.
        const allOption = allKnownOptions.find((o) => o.value == null);
        const values: T[] = Array.isArray(value) ? (value as T[]).filter((v) => v != null) : [];
        const selectedValues: T[] = allOption && values.length === 0 ? [allOption.value] : values;
        const selectedOptions: Option<T>[] = selectedValues.map(
            (v) => allKnownOptions.find((o) => o.value === v) ?? { value: v, label: `Introuvable (${String(v)})` },
        );

        const handleToggle = (optionValue: T) => {
            if (optionValue == null) {
                onChangeMulti([]);
            } else if (selectedValues.includes(optionValue)) {
                onChangeMulti(selectedValues.filter((v) => v !== optionValue));
            } else {
                onChangeMulti([...selectedValues.filter((v) => v != null), optionValue]);
            }
        };

        const handleRemove = (optionValue: T, e: React.MouseEvent) => {
            e.stopPropagation();
            onChangeMulti(selectedValues.filter((v) => v !== optionValue));
        };

        const handleClear = (e: React.MouseEvent) => {
            e.stopPropagation();
            onChangeMulti([]);
        };

        return (
            <div className={cn('flex flex-col gap-[5px]', className)} style={{ width: width || '100%' }}>
                {label || sublabel ? (
                    <div className="flex flex-col">
                        {label ? <TotemLabel>{label}</TotemLabel> : null}
                        {sublabel ? <span className="text-muted-foreground text-[13px]">{sublabel}</span> : null}
                    </div>
                ) : null}
                <div className="relative text-black" data-test={dataTest}>
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild disabled={disabled}>
                            <button
                                type="button"
                                className="flex min-h-[48px] w-full items-center justify-between gap-1.5 rounded-lg border border-input bg-transparent px-3 py-2 text-base transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                                    {selectedOptions.length > 0 ? (
                                        selectedOptions.map((option) => (
                                            <Badge
                                                key={String(option.value)}
                                                variant="secondary"
                                                className="max-w-full shrink gap-1"
                                            >
                                                <span className="truncate">{option.label}</span>
                                                {option.value != null ? (
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        className="cursor-pointer rounded-sm hover:bg-muted"
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => handleRemove(option.value, e)}
                                                    >
                                                        <XIcon className="size-3" />
                                                    </span>
                                                ) : null}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="truncate text-muted-foreground">{placeholder}</span>
                                    )}
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                    {isClearable && selectedOptions.length > 0 ? (
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={handleClear}
                                        >
                                            <XIcon className="size-4" />
                                        </span>
                                    ) : null}
                                    <ChevronDownIcon className="size-4 text-muted-foreground" />
                                </div>
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            className="w-[--radix-popover-trigger-width] p-0"
                            align="start"
                            onWheelCapture={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <Command shouldFilter={!isAsync}>
                                <CommandInput
                                    placeholder="Rechercher..."
                                    value={isAsync ? query : undefined}
                                    onValueChange={isAsync ? setQuery : undefined}
                                />
                                <CommandList>
                                    {loading ? (
                                        <div className="flex items-center justify-center py-6">
                                            <LoaderIcon className="size-4 animate-spin text-muted-foreground" />
                                        </div>
                                    ) : null}
                                    {!loading ? <CommandEmpty>Aucun résultat</CommandEmpty> : null}
                                    {!loading
                                        ? options.map((option) => {
                                              const isSelected = selectedValues.includes(option.value);
                                              return (
                                                  <CommandItem
                                                      key={String(option.value)}
                                                      value={String(option.value)}
                                                      keywords={[option.label]}
                                                      data-checked={isSelected}
                                                      onSelect={() => handleToggle(option.value)}
                                                  >
                                                      {option.label}
                                                  </CommandItem>
                                              );
                                          })
                                        : null}
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
        );
    }

    // --- Single-select ---
    const onChangeSingle = onChange as (value: T | null) => void;
    const singleValue = value as T | null;

    const selectedOption =
        options.find((o) => o.value === singleValue) ?? defaultOptions?.find((o) => o.value === singleValue) ?? null;

    const handleSelect = (optionValue: T) => {
        onChangeSingle(optionValue);
        setOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChangeSingle(null);
    };

    return (
        <div className={cn('flex flex-col gap-[5px]', className)} style={{ width: width || '100%' }}>
            {label || sublabel ? (
                <div className="flex flex-col">
                    {label ? <TotemLabel>{label}</TotemLabel> : null}
                    {sublabel ? <span className="text-muted-foreground text-[13px]">{sublabel}</span> : null}
                </div>
            ) : null}
            <div className="relative text-black" data-test={dataTest}>
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild disabled={disabled}>
                        <button
                            type="button"
                            className="flex h-[48px] w-full items-center justify-between gap-1.5 rounded-lg border border-input bg-transparent px-3 py-3 text-base transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {selectedOption ? (
                                <span className="truncate">{selectedOption.label}</span>
                            ) : (
                                <span className="text-muted-foreground">{placeholder}</span>
                            )}
                            <div className="flex shrink-0 items-center gap-1">
                                {isClearable && selectedOption ? (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onClick={handleClear}
                                    >
                                        <XIcon className="size-4" />
                                    </span>
                                ) : null}
                                <ChevronDownIcon className="size-4 text-muted-foreground" />
                            </div>
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-[--radix-popover-trigger-width] p-0"
                        align="start"
                        onWheelCapture={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <Command shouldFilter={!isAsync}>
                            <CommandInput
                                placeholder="Rechercher..."
                                value={isAsync ? query : undefined}
                                onValueChange={isAsync ? setQuery : undefined}
                            />
                            <CommandList>
                                {loading ? (
                                    <div className="flex items-center justify-center py-6">
                                        <LoaderIcon className="size-4 animate-spin text-muted-foreground" />
                                    </div>
                                ) : null}
                                {!loading ? <CommandEmpty>Aucun résultat</CommandEmpty> : null}
                                {!loading
                                    ? options.map((option) => (
                                          <CommandItem
                                              key={String(option.value)}
                                              value={String(option.value)}
                                              keywords={[option.label]}
                                              data-checked={option.value === singleValue}
                                              disabled={option.isDisabled}
                                              onSelect={() => handleSelect(option.value)}
                                          >
                                              {option.label}
                                          </CommandItem>
                                      ))
                                    : null}
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
}
